import React, { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'

// Let's include only h2 and h3 to keep the table not too long
const TAGS_TO_CONTENTS = 'h2, h3'

const TableOfContents = ({ selector, controller }) => {
  const [headings, setHeadings] = useState([])
  const [activeId, setActiveId] = useState(null)
  const tocRef = useRef(null)

  useEffect(() => {
    const content = document.querySelector(selector)
    if (content) {
      const headingElements = content.querySelectorAll(TAGS_TO_CONTENTS)
      const headingsArray = Array.from(headingElements).map((heading) => ({
        id: heading.id,
        text: heading.innerText,
      }))
      setHeadings(headingsArray)
    }
  }, [selector, controller.htmlContent])

  useEffect(() => {
    if (!headings.length) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
            window.history.replaceState(null, '', `#${entry.target.id}`)
          }
        })
      },
      { threshold: 1 },
    )

    const headingElements = document.querySelectorAll(TAGS_TO_CONTENTS)
    headingElements.forEach((element) => observer.observe(element))

    return () => {
      headingElements.forEach((element) => observer.unobserve(element))
    }
  }, [headings])

  useEffect(() => {
    if (activeId && tocRef.current) {
      const activeElement = document.getElementById(`toc-${activeId}`)
      if (activeElement) {
        const {
          offsetTop: elementOffsetTop,
          offsetHeight: elementHeight,
        } = activeElement
        const { scrollTop, clientHeight: tocClientHeight } = tocRef.current

        const isElementInView =
          elementOffsetTop >= scrollTop &&
          elementOffsetTop + elementHeight <= scrollTop + tocClientHeight

        if (!isElementInView) {
          tocRef.current.scrollTo({
            top: elementOffsetTop - tocRef.current.offsetTop,
            behavior: 'smooth',
          })
        }
      }
    }
  }, [activeId])

  useEffect(() => {
    const hash = window.location.hash
    if (hash) {
      const element = document.querySelector(hash)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setActiveId(hash.substring(1))
      }
    }
  }, [headings])

  const handleClick = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'instant', block: 'center' })
      window.history.replaceState(null, '', `#${id}`)
      setActiveId(id)
    }
  }

  if (!controller.htmlContent) return null

  return (
    <div
      id="content-table"
      ref={tocRef}
      className="fixed top-[164px] left-[16px] w-full min-w-[200px] max-w-[240px] max-h-[430px] bg-neutral_1 overflow-y-auto scrollbar scrollbar-thin scrollbar-thumb-paper scrollbar-track-neutral_1 p-4"
      style={{ zIndex: 200 }}
    >
      <h2 className="text-lg font-semibold text-text mb-2">
        Table of Contents
      </h2>
      <div className="border-t border-paper my-2"></div>
      {headings.map((heading) => (
        <div
          key={heading.id}
          id={`toc-${heading.id}`}
          className="text-sm text-text_secondary hover:text-primary mb-2 cursor-pointer"
          style={{ padding: '10px', cursor: 'pointer' }}
          onClick={() => handleClick(heading.id)}
        >
          {activeId === heading.id ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="border-l-4 border-primary bg-canvas px-[10px] py-[5px]"
            >
              {heading.text}
            </motion.div>
          ) : (
            heading.text
          )}
        </div>
      ))}
    </div>
  )
}

export default TableOfContents
